import React, { useState, useEffect } from 'react';
import './styles.scss';
import question_data from '../../utils/data';
import Header from '../../components/Header';
import Question from '../../components/Question';
import Answer from '../../components/Answer';
import Footer from '../../components/Footer';
import ReviewPage from '../ReviewPage';
import { checkEligibility } from '../../utils/checkEligibility';
import { checkCourseApplication } from '../../utils/checkCourseApplication';
import StageOneIneligible from '../../compositions/StageOneIneligible';
import Eligible from '../../compositions/Eligible';
import Ineligible from '../../compositions/Ineligible';

const EligibilityChecker = () => {
    const [currentQuestion, setCurrentQuestion] = useState(1);
    const [answers, setAnswers] = useState({});
    const [ineligible, setIneligible] = useState(false);
    const [eligible, setEligible] = useState(false);
    const [stageOneEligible, setStageOneEligible] = useState();
    const [showReviewPage, setShowReviewPage] = useState(false);
    const [questionJump, setQuestionJump] = useState(false);
    const [courseApplication, setCourseApplication] = useState(false);

    useEffect(() => {
        if (currentQuestion === 1) {
            return;
        }

        const scrollEl = document.querySelector('[data-behaviour="nu-partners-app"]');
        const scrollPos = scrollEl && scrollEl.offsetTop ? scrollEl.offsetTop : 0;

        window.scrollTo({
            top: scrollPos,
            behavior: 'smooth',
        });
    }, [currentQuestion]);

    const changeScreen = (screen) => {
        const currentAnswer = answers.hasOwnProperty(currentQuestion) ? answers[currentQuestion] : null;

        switch (screen) {
            case 'reset':
                resetForm();
                break;
            case 'continue':
                checkEligibility(answers, currentQuestion, setStageOneEligible, setIneligible, setEligible);
                checkCourseApplication(answers, currentQuestion, setCourseApplication);

                if (eligible === false && ineligible === false) {
                    if (currentAnswer && currentAnswer.hasOwnProperty('nextQuestion')) {
                        setCurrentQuestion(currentAnswer.nextQuestion);
                    } else if (question_data[currentQuestion].hasOwnProperty('nextQuestion')) {
                        setCurrentQuestion(question_data[currentQuestion].nextQuestion);
                    }
                }
                if (currentQuestion === 3) {
                    setQuestionJump(true);
                }
                break;
            case 'skip':
                // This catches all stage 2 questions.
                if (question_data[currentQuestion].hasOwnProperty('nextQuestion')) {
                    // Every question in stage 2 except for the final one.
                    if (question_data[currentQuestion].nextQuestion !== null) {
                        setCurrentQuestion(question_data[currentQuestion].nextQuestion);
                        setQuestionJump(false);
                    }
                    // The final question
                    else {
                        setIneligible(true);
                    }
                }
                break;
            case 'back':
                if (questionJump && answers && answers[4].text.length > 1) {
                    setCurrentQuestion(4);
                } else {
                    if (question_data[currentQuestion].hasOwnProperty('previousQuestion')) {
                        setCurrentQuestion(question_data[currentQuestion].previousQuestion);
                    }
                    if (currentQuestion === 5) {
                        setQuestionJump(true);
                    }
                }
                break;
            case 'review':
                setShowReviewPage(true);

                setCurrentQuestion(null);
                setIneligible(false);
                setEligible(false);
                break;
            case 'submit':
                // @todo Handle submit.
                break;
            default:
                break;
        }
    };

    const onHandleChangeLinkClick = (question_number) => {
        if (question_number) {
            setCurrentQuestion(question_number);
            setIneligible(false);
            setEligible(false);
            setShowReviewPage(false);
        }
    };

    /**
     * The window. at the end is for getting it done quickly. Probably better resetting the stage values but
     * was unable to get it working soon enough.
     */
    const resetForm = () => {
        setCurrentQuestion(1);
        setAnswers({});
        setIneligible(false);
        setEligible(false);
        setShowReviewPage(false);

        window.location.reload(false);
    };

    const continueButtonText = () => {
        // Question 4 - the one at the start of stage 2 that allows user to jump to another question.
        if (currentQuestionData.hasOwnProperty('nonQuestion') && currentQuestionData.nonQuestion === true) {
            return 'Continue to eligibility criteria';
        }
        // Stage 1 questions.
        else if (question_data[currentQuestion].stage === 1) {
            return 'Continue';
        }
        // If selected answer for question itself has sub-question
        else if (
            typeof answers[currentQuestion] !== 'undefined' &&
            answers[currentQuestion].hasOwnProperty('subQuestion')
        ) {
            const answer_key = answers[currentQuestion].key;

            // If the sub-question has been answered.
            if (answers[currentQuestion].hasOwnProperty(answer_key)) {
                return 'Continue';
            }

            // If the sub-question has NOT been answered.
            return 'Skip';
        }
        // If the question has no sub-question and has an answer selected.
        else if (typeof answers[currentQuestion] !== 'undefined') {
            return 'Continue';
        }

        return 'Skip';
    };

    /**
     * Store the answers selected by the user.
     *
     * @param answer_key
     *   Answer array key.
     * @param answer
     *   Selected answer object.
     * @param question
     *   Question number to assign answer to.
     * @param parentAnswerKey
     *   Used if this is the answer to a sub-question so this answer will get stored as a child of the
     *   parent answer object.
     */
    const updateAnswers = (answer_key, answer, question, parentAnswerKey) => {
        const currentAnswers = { ...answers };

        // Key property added to selected answers to match against the key for answers related to questions.
        answer.key = parseInt(answer_key);

        // Used to store answers for sub questions.
        if (parentAnswerKey) {
            currentAnswers[question][parentAnswerKey] = answer;
        }
        // Stores answers to main / top level questions.
        else {
            currentAnswers[question] = answer;
        }

        setAnswers(currentAnswers);
    };

    /**
     * Remove the answer from storage.
     *
     * @todo Get this to actually work!
     *
     * @param question
     *   Question to remove stored answers from.
     */
    const removeAnswer = (question) => {
        const currentAnswers = { ...answers };

        if (currentAnswers.hasOwnProperty(question)) {
            delete currentAnswers[question];
            if (question === 12) {
                setAnswers(answers);
            } else {
                setAnswers(currentAnswers);
            }
        }
    };

    const currentQuestionData = question_data[currentQuestion];
    const headerProgressStage =
        currentQuestionData && currentQuestionData.hasOwnProperty('stage') && currentQuestionData.stage === 1
            ? currentQuestion
            : 3;
    const headerHeadingType = ineligible || eligible ? 'bold' : 'normal';
    const questionDescriptionList =
        currentQuestionData && currentQuestionData.hasOwnProperty('descriptionList')
            ? currentQuestionData.descriptionList
            : '';
    const answerAnswerDescription =
        currentQuestionData && currentQuestionData.hasOwnProperty('answerDescription')
            ? currentQuestionData.answerDescription
            : '';
    const footerBackButtonText =
        currentQuestion === 1
            ? 'Back'
            : currentQuestionData &&
              currentQuestionData.hasOwnProperty('nonQuestion') &&
              currentQuestionData.nonQuestion === true
            ? ''
            : 'Back';
    const footerAboveFooterText =
        currentQuestionData && currentQuestionData.hasOwnProperty('aboveFooterText')
            ? currentQuestionData.aboveFooterText
            : '';
    const footerAboveFooterTextSize =
        currentQuestionData && currentQuestionData.hasOwnProperty('aboveFooterTextSize')
            ? currentQuestionData.aboveFooterTextSize
            : '';
    const selectedAnswer = answers.hasOwnProperty(currentQuestion) ? answers[currentQuestion] : null;

    if (showReviewPage) {
        return (
            <ReviewPage
                questions={question_data}
                answers={answers}
                updateAnswers={updateAnswers}
                setIneligible={setIneligible}
                setEligible={setEligible}
                headerHeadingType={headerHeadingType}
                onHandleChangeLinkClick={onHandleChangeLinkClick}
            />
        );
    } else if (stageOneEligible === false) {
        return <StageOneIneligible type='stage_one' handleFooterLinkClick={changeScreen} />;
    } else if (ineligible) {
        return <Ineligible handleFooterLinkClick={changeScreen} />;
    } else if (eligible) {
        return <Eligible handleFooterLinkClick={changeScreen} courseApplication={courseApplication} />;
    }

    return (
        <div className='wrapper'>
            <Header
                title={currentQuestionData.title}
                heading={currentQuestionData.heading}
                progressStage={headerProgressStage}
            />
            <Question
                heading={currentQuestionData.question}
                description={currentQuestionData.description}
                list={currentQuestionData.list}
                list2={currentQuestionData.list2}
                descriptionList={questionDescriptionList}
            />
            <Answer
                currentQuestion={currentQuestion}
                type={currentQuestionData.type}
                answers={currentQuestionData.answers}
                storeAnswer={updateAnswers}
                removeAnswer={removeAnswer}
                selectedAnswer={selectedAnswer}
                parentAnswerKey={null}
                subQuestion={false}
            />
            <p className='answerDescription' dangerouslySetInnerHTML={{ __html: answerAnswerDescription }} />
            <Footer
                backButtonText={footerBackButtonText}
                backToPartners={footerBackButtonText}
                backButtonScreen='back'
                forwardButtonText={continueButtonText()}
                forwardButtonScreen={typeof answers[currentQuestion] !== 'undefined' ? 'continue' : 'skip'}
                changeScreen={changeScreen}
                aboveFooterText={footerAboveFooterText}
                aboveFooterTextSize={footerAboveFooterTextSize}
                isForwardButtonEnable={selectedAnswer && Object.keys(selectedAnswer).length > 0}
                currentQuestion={currentQuestion}
                selectedAnswer={answers}
            />
        </div>
    );
};

export default EligibilityChecker;
